'use client'

import dynamic from 'next/dynamic'
import { C, Exercise, Figure, H, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/probability'

const CoinFlipper = dynamic(() => import('../_components/widgets/ProbWidgets').then(m => m.CoinFlipper), { ssr: false })

function DiceGrid() {
  return (
    <div className="overflow-x-auto">
      <table className="mx-auto border-separate" style={{ borderSpacing: 4 }}>
        <thead>
          <tr>
            <th className="gd-label !text-[10px]">+</th>
            {[1, 2, 3, 4, 5, 6].map(b => (
              <th key={b} className="gd-num text-[13px]" style={{ color: 'var(--gd-amber)' }}>
                {b}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5, 6].map(a => (
            <tr key={a}>
              <th className="gd-num pr-1 text-[13px]" style={{ color: 'var(--gd-blue)' }}>
                {a}
              </th>
              {[1, 2, 3, 4, 5, 6].map(b => (
                <td
                  key={b}
                  className="gd-num h-9 w-9 rounded-[6px] text-center text-[14px]"
                  style={{ background: a + b === 7 ? 'var(--gd-accent)' : 'var(--gd-bg-2)', color: a + b === 7 ? 'var(--gd-accent-ink)' : 'var(--gd-ink-2)', fontWeight: a + b === 7 ? 700 : 400 }}
                >
                  {a + b}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function ProbabilityBasics() {
  return (
    <Lesson>
      <Step>
        <H>A number for “how likely”</H>
        <P>
          A <strong>probability</strong> is a number between 0 and 1 that says how likely something is. 0 means “never”, 1 means
          “always”, 0.5 means “half the time”. You can also read it as a fraction of the time: <em>in the long run</em>, an
          event with probability 0.3 happens in about 30% of tries.
        </P>
        <P>
          Machine-learning models don’t really give answers — they give probabilities. “90% cat, 8% dog, 2% something else.”
          “The next word is <em>the</em> with probability 0.21.” So we need to be fluent in them.
        </P>
      </Step>

      <Step gate="q-lln">
        <H>Randomness is predictable in bulk</H>
        <P>
          One coin flip is a mystery. Ten thousand are not. Flip a fair coin a few times and the fraction of heads jumps all over
          the place; flip it thousands of times and the fraction settles down on the true probability. This is the{' '}
          <strong>law of large numbers</strong>.
        </P>
        <Figure n="5.1" caption="The running fraction of heads. Early on it swings wildly; with enough flips it hugs the dashed line (the true probability). Try a biased coin.">
          <CoinFlipper />
        </Figure>
        <Exercise id="simulate-coin" title="Flip ten thousand coins in code" {...EX.simulateCoin} />
        <Quiz
          id="q-lln"
          question="You flip a fair coin 10 times and get 7 heads. What’s the best explanation?"
          options={[
            { label: 'Normal randomness — small samples swing a lot.', correct: true },
            { label: 'The coin must be biased toward heads.', why: 'With only 10 flips, 7 heads happens quite often (about 1 in 6 times) with a fair coin.' },
            { label: 'Tails is now “due”, so the next flip is probably tails.', why: 'Coins have no memory. This is the gambler’s fallacy.' },
          ]}
          explain="Small samples are noisy. Machine learning has the same issue: a model tested on 10 examples tells you little; on 10,000, a lot."
        />
      </Step>

      <Step gate={['nq-seven', 'exact-count']}>
        <H>Counting equally likely outcomes</H>
        <P>When every outcome is equally likely, probability is just counting:</P>
        <MB>{'P(\\text{event}) = \\frac{\\text{number of ways it can happen}}{\\text{number of possible outcomes}}'}</MB>
        <P>Two dice have 6 × 6 = 36 equally likely outcomes. How many of them add up to 7?</P>
        <Figure n="5.2" caption="Every outcome of two dice. The highlighted cells sum to 7.">
          <DiceGrid />
        </Figure>
        <NumberQuiz id="nq-seven" question={<>What is the probability that two dice sum to 7? (A fraction like 1/6 is fine.)</>} answer={1 / 6} tolerance={1e-3} explain={<>6 ways out of 36 = 1/6 ≈ 0.167. Seven is the most likely sum.</>} />
        <Exercise
          id="exact-count"
          title="Count it in code"
          {...EX.exactCount}
          hints={[
            <>
              Two nested loops: <C>{'for (let a = 1; a <= 6; a++)'}</C> and the same for <C>b</C> inside.
            </>,
            <>
              Inside: <C>total++</C> every time, and <C>favourable++</C> when <C>a + b === target</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate={['q-and', 'dice-seven']}>
        <H>Combining probabilities</H>
        <ul className="gd-list">
          <li>
            <strong>NOT:</strong> <M>{'P(\\text{not } A) = 1 - P(A)'}</M>. If rain is 30% likely, no rain is 70%.
          </li>
          <li>
            <strong>AND</strong> (independent events): multiply. Two heads in a row: <M>{'\\tfrac{1}{2} \\times \\tfrac{1}{2} = \\tfrac{1}{4}'}</M>.
          </li>
          <li>
            <strong>OR</strong> (can’t both happen): add. Rolling a 1 or a 2: <M>{'\\tfrac{1}{6} + \\tfrac{1}{6} = \\tfrac{1}{3}'}</M>.
          </li>
        </ul>
        <Quiz
          id="q-and"
          question="What’s the probability of rolling three sixes in a row?"
          options={[{ label: '1/216', correct: true }, { label: '3/6', why: 'That would be adding. For “this AND this AND this”, multiply.' }, { label: '1/18', why: 'That’s 1/6 × 1/3. Each roll is 1/6: (1/6)³.' }]}
          explain="(1/6)³ = 1/216. Multiplying many probabilities makes them tiny fast — which is why language models use logarithms, as you saw in Module 2."
        />
        <P>When counting is too hard, simulate! Run the experiment many times with a random number generator and count. This is called a <strong>Monte Carlo</strong> estimate.</P>
        <Exercise id="dice-seven" title="Estimate by simulation" {...EX.diceSeven} hints={[<>Roll with <C>random.int(1, 7)</C> twice and compare the sum to 7.</>]} />
      </Step>

      <Step gate="q-cond">
        <H>“Given that…”</H>
        <P>
          The most important kind of probability in AI is <strong>conditional</strong>: the probability of something{' '}
          <em>given</em> that you already know something else. It’s written with a bar:
        </P>
        <MB>{'P(\\text{rain} \\mid \\text{cloudy})'}</MB>
        <P>
          “The probability of rain, given that it’s cloudy” — much higher than the probability of rain on a random day. Knowing
          things changes the odds.
        </P>
        <Quiz
          id="q-cond"
          question="A language model reading “The cat sat on the” is computing…"
          options={[
            { label: 'P(next word | “The cat sat on the”)', correct: true },
            { label: 'P(“The cat sat on the”)', why: 'It already has that text — it’s predicting what comes after it.' },
            { label: 'P(cat)', why: 'It’s predicting the next word, conditioned on all the words so far.' },
          ]}
          explain="Every language model, from a simple counter to the biggest, is a machine for estimating P(next token | everything before it). You’ll build several."
        />
      </Step>
    </Lesson>
  )
}
